/**
 * Shared icon registry for the layouts module.
 *
 * Single source of truth used by:
 *   - CategoryForm (icon picker)
 *   - LayoutCard   (category icon rendering)
 *   - LayoutSidebar (category icon rendering)
 */

import { ComponentType, CSSProperties, ReactElement } from 'react';
import AppsIcon from '@mui/icons-material/Apps';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BrightnessMediumIcon from '@mui/icons-material/BrightnessMedium';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CodeIcon from '@mui/icons-material/Code';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import DescriptionIcon from '@mui/icons-material/Description';
import DevicesIcon from '@mui/icons-material/Devices';
import DnsIcon from '@mui/icons-material/Dns';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ForumIcon from '@mui/icons-material/Forum';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import HealingIcon from '@mui/icons-material/Healing';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import LabelIcon from '@mui/icons-material/Label';
import LayersIcon from '@mui/icons-material/Layers';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LockIcon from '@mui/icons-material/Lock';
import MemoryIcon from '@mui/icons-material/Memory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Router as RouterIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Tune as TuneIcon,
  VpnKey as VpnKeyIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material/SvgIcon';

export interface IconEntry {
  key: string;
  label: string;
  Component: ComponentType<SvgIconProps>;
}

/** Full list of icons available in the picker. */
export const ICONS: IconEntry[] = [
  { key: 'Apps', label: 'Apps', Component: AppsIcon },
  { key: 'Assessment', label: 'Assessment', Component: AssessmentIcon },
  { key: 'Assignment', label: 'Assignment', Component: AssignmentIcon },
  { key: 'AttachMoney', label: 'Money', Component: AttachMoneyIcon },
  { key: 'AccountBalance', label: 'Bank / Finance', Component: AccountBalanceIcon },
  { key: 'AccountCircle', label: 'Account', Component: AccountCircleIcon },
  { key: 'Bookmark', label: 'Bookmark', Component: BookmarkIcon },
  { key: 'BrightnessMedium', label: 'Theme', Component: BrightnessMediumIcon },
  { key: 'Build', label: 'Build / Tools', Component: BuildIcon },
  { key: 'BugReport', label: 'Bug Report', Component: BugReportIcon },
  { key: 'Business', label: 'Business', Component: BusinessIcon },
  { key: 'Category', label: 'Category', Component: CategoryIcon },
  { key: 'Chat', label: 'Chat', Component: ChatIcon },
  { key: 'CheckCircle', label: 'Check / Done', Component: CheckCircleIcon },
  { key: 'Cloud', label: 'Cloud', Component: CloudIcon },
  { key: 'CloudUpload', label: 'Cloud Upload', Component: CloudUploadIcon },
  { key: 'Code', label: 'Code', Component: CodeIcon },
  { key: 'Dashboard', label: 'Dashboard', Component: DashboardIcon },
  { key: 'DataUsage', label: 'Data Usage', Component: DataUsageIcon },
  { key: 'Description', label: 'Document', Component: DescriptionIcon },
  { key: 'Devices', label: 'Devices', Component: DevicesIcon },
  { key: 'Dns', label: 'DNS / Server', Component: DnsIcon },
  { key: 'Email', label: 'Email', Component: EmailIcon },
  { key: 'Extension', label: 'Extension', Component: ExtensionIcon },
  { key: 'Favorite', label: 'Favourite', Component: FavoriteIcon },
  { key: 'FlashOn', label: 'Flash / Quick', Component: FlashOnIcon },
  { key: 'Folder', label: 'Folder', Component: FolderIcon },
  { key: 'FolderShared', label: 'Shared Folder', Component: FolderSharedIcon },
  { key: 'Forum', label: 'Forum', Component: ForumIcon },
  { key: 'Gavel', label: 'Legal / Gavel', Component: GavelIcon },
  { key: 'Group', label: 'Group / Team', Component: GroupIcon },
  { key: 'Healing', label: 'Healing / Health', Component: HealingIcon },
  { key: 'History', label: 'History', Component: HistoryIcon },
  { key: 'Info', label: 'Info', Component: InfoIcon },
  { key: 'Label', label: 'Label / Tag', Component: LabelIcon },
  { key: 'Layers', label: 'Layers / Stack', Component: LayersIcon },
  { key: 'LibraryBooks', label: 'Library / Books', Component: LibraryBooksIcon },
  { key: 'LocalHospital', label: 'Hospital', Component: LocalHospitalIcon },
  { key: 'Lock', label: 'Lock / Security', Component: LockIcon },
  { key: 'Memory', label: 'Memory / Chip', Component: MemoryIcon },
  { key: 'MenuBook', label: 'Docs / Manual', Component: MenuBookIcon },
  { key: 'MonetizationOn', label: 'Monetisation', Component: MonetizationOnIcon },
  { key: 'Notifications', label: 'Notifications', Component: NotificationsIcon },
  { key: 'Payment', label: 'Payment', Component: PaymentIcon },
  { key: 'People', label: 'People', Component: PeopleIcon },
  { key: 'Person', label: 'Person / User', Component: PersonIcon },
  { key: 'Public', label: 'Public / Globe', Component: PublicIcon },
  { key: 'Router', label: 'Network / Router', Component: RouterIcon },
  { key: 'Schedule', label: 'Schedule', Component: ScheduleIcon },
  { key: 'School', label: 'Education', Component: SchoolIcon },
  { key: 'Security', label: 'Security', Component: SecurityIcon },
  { key: 'Settings', label: 'Settings', Component: SettingsIcon },
  { key: 'Speed', label: 'Speed / Perf', Component: SpeedIcon },
  { key: 'Star', label: 'Star / Featured', Component: StarIcon },
  { key: 'Storage', label: 'Storage / DB', Component: StorageIcon },
  { key: 'Timeline', label: 'Timeline', Component: TimelineIcon },
  { key: 'TrendingUp', label: 'Trending / Growth', Component: TrendingUpIcon },
  { key: 'Tune', label: 'Tune / Config', Component: TuneIcon },
  { key: 'VpnKey', label: 'VPN / Key', Component: VpnKeyIcon },
  { key: 'Work', label: 'Work', Component: WorkIcon },
];

/**
 * Map of icon key -> React component. Used by display components to render
 * icons stored as "mui:Key" strings.
 */
export const ICON_COMPONENT_MAP: Record<string, ComponentType<SvgIconProps>> =
  Object.fromEntries(ICONS.map(e => [e.key, e.Component]));

/** Brand / SVG icons served from /public/icons/ — value stored as "/icons/filename.svg" */
export const SVG_ICONS: { path: string; label: string }[] = [
  { path: '/icons/aws.svg',         label: 'AWS' },
  { path: '/icons/azure.svg',        label: 'Azure' },
  { path: '/icons/atlassian.svg',    label: 'Atlassian' },
  { path: '/icons/confluence.svg',   label: 'Confluence' },
  { path: '/icons/fossa.svg',        label: 'FOSSA' },
  { path: '/icons/github.svg',       label: 'GitHub' },
  { path: '/icons/grafana.svg',      label: 'Grafana' },
  { path: '/icons/harness.svg',      label: 'Harness' },
  { path: '/icons/jira.svg',         label: 'Jira' },
  { path: '/icons/oneadvanced.svg',  label: 'OneAdvanced' },
  { path: '/icons/servicenow.svg',   label: 'ServiceNow' },
  { path: '/icons/sonarqube.svg',    label: 'SonarQube' },
];

export const SVG_ICON_PATHS = new Set(SVG_ICONS.map(i => i.path));

/**
 * Renders an icon value that may be:
 *   "mui:Key"       — a registered MUI icon
 *   "/path/to.svg"  — an SVG img fallback
 *   undefined       — returns null
 */
export function renderCategoryIcon(
  iconValue: string | undefined,
  props: SvgIconProps & { imgStyle?: CSSProperties } = {},
): ReactElement | null {
  if (!iconValue) return null;
  const { imgStyle, ...svgProps } = props;
  if (iconValue.startsWith('mui:')) {
    const key = iconValue.slice(4);
    const Comp = ICON_COMPONENT_MAP[key];
    if (Comp) return <Comp {...svgProps} />;
    // Unknown key — fall back to Category icon
    return <CategoryIcon {...svgProps} />;
  }
  const style: CSSProperties = {
    width: svgProps.style?.fontSize ?? 20,
    height: svgProps.style?.fontSize ?? 20,
    objectFit: 'contain',
    ...imgStyle,
  };
  return <img src={iconValue} alt="" style={style} />;
}
